import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import './Navbar.css';
import { FaSun, FaMoon } from 'react-icons/fa';
import logoWhite from '../assets/images/logo-w.png'; // White logo for dark theme
import logoBlack from '../assets/images/logo-b.png'; // Black logo for light theme

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
    setTheme(savedTheme);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    const handleScroll = () => setScrolled(window.scrollY > 20);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMenuOpen(false);
  };

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);
  const isActiveRoute = (path) => location.pathname === path;

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', nextTheme);
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);
  };

  const NavLink = ({ to, children, onClick, className = '' }) => (
    <Link to={to} onClick={onClick} className={`${className} ${isActiveRoute(to) ? 'active' : ''}`}>
      {children}
    </Link>
  );

  // Choose logo based on current theme
  const currentLogo = theme === 'dark' ? logoWhite : logoBlack;

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbarContainer">
        <Link to="/" className="brand">
          <div className="brandIcon">
            <img src={currentLogo} alt="Wahler Analytics" className="brandLogo" />
          </div>

        </Link>

        {!isMobile ? (
          <div className="desktopLinks">
            <div className="navLinks">
              {user ? (
                <>
                  <NavLink to="/dashboard" className="navLink">
                    <i className="fas fa-tachometer-alt"></i>
                    <span>Dashboard</span>
                  </NavLink>
                  {user.role === 'user' && (
                    <NavLink to="/subscribe" className="navLink">
                      <i className="fas fa-crown"></i>
                      <span>Subscribe</span>
                    </NavLink>
                  )}
                  {user.role === 'subscriber' && (
                    <>
                      <NavLink to="/subscribe" className="navLink">
                        <i className="fas fa-user-check"></i>
                        <span>Subscription</span>
                      </NavLink>
                      <NavLink to="/lessons" className="navLink">
                        <i className="fas fa-book-open"></i>
                        <span>Lessons</span>
                      </NavLink>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <NavLink to="/admin/users" className="navLink">
                        <i className="fas fa-users"></i>
                        <span>Users</span>
                      </NavLink>
                      <NavLink to="/admin/lessons" className="navLink">
                        <i className="fas fa-edit"></i>
                        <span>Lessons</span>
                      </NavLink>
                      <NavLink to="/admin/categories" className="navLink">
                        <i className="fas fa-tags"></i>
                        <span>Categories</span>
                      </NavLink>
                    </>
                  )}
                </>
              ) : (
                <>
                  <NavLink to="/login" className="navLink">
                    <i className="fas fa-sign-in-alt"></i>
                    <span>Login</span>
                  </NavLink>
                  <NavLink to="/register" className="navLink">
                    <i className="fas fa-user-plus"></i>
                    <span>Register</span>
                  </NavLink>
                </>
              )}
              <button onClick={toggleTheme} className="navLink" aria-label="Toggle Theme">
                {theme === 'light' ? <FaMoon /> : <FaSun />}
              </button>
            </div>

            {user && (
              <div className="userSection">
                <div className="userInfo">
                  <div className="userAvatar">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="userDetails">
                    <span className="userName">{user.name || 'User'}</span>
                    <span className="userRole">{user.role}</span>
                  </div>
                </div>
                <button onClick={handleLogout} className="logoutButton">
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mobileMenuWrapper">
            {user && (
              <div className="mobileUserInfo">
                <div className="userAvatar small">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
              </div>
            )}
            <button className="mobileMenuButton" onClick={toggleMenu}>
              ☰
            </button>
            {menuOpen && (
              <>
                <div className="mobileOverlay" onClick={closeMenu}></div>
                <div className={`mobileMenu ${menuOpen ? 'open' : ''}`}>
                  {user && (
                    <div className="mobileUserSection">
                      <div className="userAvatar large">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="userDetails">
                        <span className="userName">{user.name || 'User'}</span>
                        <span className="userRole">{user.role}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="mobileLinks">
                    {user ? (
                      <>
                        <NavLink to="/dashboard" onClick={closeMenu} className="mobileLink">
                          <i className="fas fa-tachometer-alt"></i>
                          <span>Dashboard</span>
                        </NavLink>
                        {user.role === 'user' && (
                          <NavLink to="/subscribe" onClick={closeMenu} className="mobileLink">
                            <i className="fas fa-crown"></i>
                            <span>Subscribe</span>
                          </NavLink>
                        )}
                        {user.role === 'subscriber' && (
                          <>
                            <NavLink to="/subscribe" onClick={closeMenu} className="mobileLink">
                              <i className="fas fa-user-check"></i>
                              <span>Subscription</span>
                            </NavLink>
                            <NavLink to="/lessons" onClick={closeMenu} className="mobileLink">
                              <i className="fas fa-book-open"></i>
                              <span>Lessons</span>
                            </NavLink>
                          </>
                        )}
                        {user.role === 'admin' && (
                          <>
                            <NavLink to="/admin/users" onClick={closeMenu} className="mobileLink">
                              <i className="fas fa-users"></i>
                              <span>Users</span>
                            </NavLink>
                            <NavLink to="/admin/lessons" onClick={closeMenu} className="mobileLink">
                              <i className="fas fa-edit"></i>
                              <span>Lessons</span>
                            </NavLink>
                            <NavLink to="/admin/categories" onClick={closeMenu} className="mobileLink">
                              <i className="fas fa-tags"></i>
                              <span>Categories</span>
                            </NavLink>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <NavLink to="/login" onClick={closeMenu} className="mobileLink">
                          <i className="fas fa-sign-in-alt"></i>
                          <span>Login</span>
                        </NavLink>
                        <NavLink to="/register" onClick={closeMenu} className="mobileLink">
                          <i className="fas fa-user-plus"></i>
                          <span>Register</span>
                        </NavLink>
                      </>
                    )}
                    
                    <button onClick={toggleTheme} className="mobileLink" aria-label="Toggle Theme">
                      {theme === 'light' ? <FaMoon /> : <FaSun />}
                      <span>Theme</span>
                    </button>
                    
                    {user && (
                      <button onClick={handleLogout} className="mobileLogoutButton">
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Logout</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;